import React, { useState, useEffect, useCallback } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box, Paper, Typography, Button, Switch, FormControlLabel,
  Card, CardContent, Grid, Alert, Snackbar,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tabs, Tab, CircularProgress,
  IconButton, Tooltip, Divider, Pagination,
  Select, MenuItem, FormControl, InputLabel, Stack
} from '@mui/material';
import {
  Casino, Refresh, EmojiEvents, Save, Bolt, Timeline
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

/**
 * 🎰 Страница «Лояльность слотов» — MoonStash копилка + MoonRush лидерборд.
 * Phase 1: видит только admin (TG 592457038).
 */
const Loyalty = () => {
  const [overview, setOverview] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsTotalPages, setPayoutsTotalPages] = useState(1);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const PAYOUTS_PER_PAGE = 36;
  // Кто сейчас в отыгрыше — поимённый список (в обзоре были только
  // счётчик и сумма). Пагинация, чтобы страница не росла без предела.
  const [wageringList, setWageringList] = useState([]);
  const [wageringPage, setWageringPage] = useState(1);
  const [wageringTotalPages, setWageringTotalPages] = useState(1);
  const [wageringTotal, setWageringTotal] = useState(0);
  const WAGERING_PER_PAGE = 25;
  const [settings, setSettings] = useState([]);
  const [settingsDraft, setSettingsDraft] = useState({}); // {key: editedValue}
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedFilters, setFeedFilters] = useState({ type: '', source: '', wager_status: '' });
  const [feedLimit, setFeedLimit] = useState(150);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [leaders, setLeaders] = useState(null);
  const [leadersLoading, setLeadersLoading] = useState(false);
  // 🤝 Реферальная система слотов
  const [refStats, setRefStats] = useState(null);
  const [refTop, setRefTop] = useState([]);
  const [refFeed, setRefFeed] = useState([]);
  const [refPairs, setRefPairs] = useState([]);
  const [refPairsPage, setRefPairsPage] = useState(1);
  const [refPairsTotalPages, setRefPairsTotalPages] = useState(1);
  const [refPairsTotal, setRefPairsTotal] = useState(0);
  const [refLoading, setRefLoading] = useState(false);
  const REF_PAIRS_PER_PAGE = 50;
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = usePersistedTab('loyalty', 0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Форма тест-грантов
  const [grantUserId, setGrantUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState('100');
  const [grantSource, setGrantSource] = useState('admin_adjust');
  const [grantSpinsUserId, setGrantSpinsUserId] = useState('');
  const [grantSpinsCount, setGrantSpinsCount] = useState('7');

  const loadOverview = useCallback(async () => {
    try {
      const res = await adminAPI.getLoyaltyOverview();
      if (res.data.success) setOverview(res.data.data);
    } catch (e) {
      console.error('Ошибка загрузки overview лояльности:', e);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    try {
      const res = await adminAPI.getLoyaltyLeaderboard();
      // API возвращает объект {topByVolume, topBySpins, pool, weekStart, me}.
      // Берём оба топа — рендерим раздельно.
      if (res.data.success) setLeaderboard(res.data.data || null);
    } catch (e) {
      console.error('Ошибка загрузки лидерборда:', e);
    }
  }, []);

  const loadWagering = useCallback(async (page = 1) => {
    try {
      const res = await adminAPI.getLoyaltyWagering(page, WAGERING_PER_PAGE);
      if (res.data.success) {
        setWageringList(res.data.data || []);
        const pg = res.data.pagination || {};
        setWageringPage(pg.page || page);
        setWageringTotalPages(pg.totalPages || 1);
        setWageringTotal(pg.total || 0);
      }
    } catch (e) {
      console.error('Ошибка загрузки списка отыгрыша:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPayouts = useCallback(async (page = 1) => {
    try {
      setPayoutsLoading(true);
      const res = await adminAPI.getLoyaltyPayoutsArchive(page, PAYOUTS_PER_PAGE);
      if (res.data.success) {
        setPayouts(res.data.data || []);
        const pg = res.data.pagination || {};
        setPayoutsPage(pg.page || page);
        setPayoutsTotalPages(pg.totalPages || 1);
        setPayoutsTotal(pg.total || 0);
      }
    } catch (e) {
      console.error('Ошибка загрузки выплат:', e);
    } finally {
      setPayoutsLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getLoyaltySettings();
      if (res.data.success) {
        setSettings(res.data.data || []);
        // Сбрасываем драфты
        setSettingsDraft({});
      }
    } catch (e) {
      console.error('Ошибка загрузки настроек:', e);
    }
  }, []);

  const loadFeed = useCallback(async (limitOverride = null) => {
    try {
      setFeedLoading(true);
      const lim = limitOverride || feedLimit;
      const res = await adminAPI.getLoyaltyFeed(lim, 0, feedFilters);
      if (res.data.success) {
        const data = res.data.data || [];
        setFeed(data);
        setFeedHasMore(data.length >= lim);
      }
    } catch (e) {
      console.error('Ошибка загрузки ленты событий:', e);
    } finally {
      setFeedLoading(false);
    }
  }, [feedFilters, feedLimit]);

  const loadMoreFeed = useCallback(() => {
    const newLimit = feedLimit + 150;
    setFeedLimit(newLimit);
    loadFeed(newLimit);
  }, [feedLimit, loadFeed]);

  const loadLeaders = useCallback(async () => {
    try {
      setLeadersLoading(true);
      const res = await adminAPI.getLoyaltyLeaders(10);
      if (res.data.success) setLeaders(res.data.data || null);
    } catch (e) {
      console.error('Ошибка загрузки лидеров лояльности:', e);
    } finally {
      setLeadersLoading(false);
    }
  }, []);

  // 🤝 Загрузка реферальной системы слотов (статистика + топ + лента + пары).
  const loadReferralPairs = useCallback(async (page = 1) => {
    try {
      const res = await adminAPI.getSlotReferralsPairs(page, REF_PAIRS_PER_PAGE);
      if (res.data.success) {
        setRefPairs(res.data.data || []);
        const pg = res.data.pagination || {};
        setRefPairsPage(pg.page || page);
        setRefPairsTotalPages(pg.totalPages || 1);
        setRefPairsTotal(pg.total || 0);
      }
    } catch (e) {
      console.error('Ошибка загрузки пар рефералки:', e);
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      setRefLoading(true);
      const [statsRes, topRes, feedRes] = await Promise.all([
        adminAPI.getSlotReferralsStats(),
        adminAPI.getSlotReferralsTop(50),
        adminAPI.getSlotReferralsFeed(100)
      ]);
      if (statsRes.data.success) setRefStats(statsRes.data.data || null);
      if (topRes.data.success) setRefTop(topRes.data.data || []);
      if (feedRes.data.success) setRefFeed(feedRes.data.data || []);
      await loadReferralPairs(1);
    } catch (e) {
      console.error('Ошибка загрузки реферальной системы слотов:', e);
    } finally {
      setRefLoading(false);
    }
  }, [loadReferralPairs]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadOverview(), loadLeaderboard(), loadPayouts(), loadSettings(), loadLeaders(), loadWagering()]);
    setLoading(false);
  }, [loadOverview, loadLeaderboard, loadPayouts, loadSettings, loadLeaders, loadWagering]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Авто-обновление ленты каждые 15 сек, только когда таб активен.
  // tabValue === 0 — таб «Лента событий» (теперь первая вкладка).
  // При смене фильтров — мгновенный перезапрос с лимитом 150 (свежий список).
  useEffect(() => {
    if (tabValue !== 0) return;
    setFeedLimit(150);
    loadFeed(150);
    const id = setInterval(() => loadFeed(), 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, feedFilters]);

  // 🤝 Загружаем рефералку при первом открытии вкладки «Рефералы» (index 6).
  useEffect(() => {
    if (tabValue === 6) loadReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const handleToggle = async () => {
    try {
      const newEnabled = !overview?.enabled;
      await adminAPI.toggleLoyalty(newEnabled);
      setSnackbar({
        open: true,
        message: `Лояльность ${newEnabled ? 'ВКЛЮЧЕНА' : 'ВЫКЛЮЧЕНА'}`,
        severity: 'success'
      });
      loadOverview();
    } catch (e) {
      setSnackbar({ open: true, message: 'Ошибка переключения', severity: 'error' });
    }
  };

  const handleGrant = async () => {
    if (!grantUserId || !grantAmount) {
      setSnackbar({ open: true, message: 'Укажите userId и сумму', severity: 'error' });
      return;
    }
    try {
      await adminAPI.grantLoyaltyTest(parseInt(grantUserId), parseFloat(grantAmount), grantSource);
      setSnackbar({
        open: true,
        message: `Зачислено ${grantAmount} SPERM в MoonStash юзеру ${grantUserId}`,
        severity: 'success'
      });
      loadOverview();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Ошибка зачисления',
        severity: 'error'
      });
    }
  };

  const handleGrantSpins = async () => {
    if (!grantSpinsUserId) {
      setSnackbar({ open: true, message: 'Укажите userId', severity: 'error' });
      return;
    }
    try {
      await adminAPI.grantLoyaltyWheelSpins(
        parseInt(grantSpinsUserId),
        parseInt(grantSpinsCount) || 1
      );
      setSnackbar({
        open: true,
        message: `Добавлено ${grantSpinsCount} вращений колеса юзеру ${grantSpinsUserId}`,
        severity: 'success'
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Ошибка добавления вращений',
        severity: 'error'
      });
    }
  };

  // Inline-редактирование настройки. Тип определяем по тому, какое поле value_* не null.
  const handleSettingChange = (key, value) => {
    setSettingsDraft(d => ({ ...d, [key]: value }));
  };

  const handleSettingSave = async (row) => {
    const draft = settingsDraft[row.key];
    if (draft === undefined) return;
    try {
      // PATCH /admin/settings/update — value_text принимается универсально на бэке (parseFloat и т.п.)
      await adminAPI.updateSetting('loyalty', row.key, draft);
      setSnackbar({ open: true, message: `Сохранено: ${row.key}`, severity: 'success' });
      loadSettings();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Ошибка сохранения',
        severity: 'error'
      });
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const fmtNum = (v) => {
    const n = parseFloat(v || 0);
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  };

  // Какое значение редактируется в строке настроек
  const settingCurrentValue = (row) => {
    if (settingsDraft[row.key] !== undefined) return settingsDraft[row.key];
    if (row.value_boolean !== null && row.value_boolean !== undefined) return String(row.value_boolean);
    if (row.value_number !== null && row.value_number !== undefined) return String(row.value_number);
    if (row.value_string !== null && row.value_string !== undefined) return row.value_string;
    return '';
  };

  // Источники начислений MoonStash — иконка и русское название.
  const SOURCE_META = {
    rakeback:     { icon: '⚡', label: 'Рейкбэк' },
    mission:      { icon: '🎯', label: 'Миссия' },
    wheel:        { icon: '🎡', label: 'Колесо' },
    wheel_double: { icon: '✨', label: 'Удвоение' },
    chest:        { icon: '📦', label: 'Сундук' },
    moonrush:     { icon: '🏆', label: 'MoonRush' },
    fs_win:       { icon: '🎰', label: 'Выигрыш с FS' },
    claim:        { icon: '💸', label: 'Собрано' },
    admin_adjust: { icon: '🛠️', label: 'Админ' },
    referral:     { icon: '🤝', label: 'Реферал' }
  };
  const sourceIcon = (src) => SOURCE_META[src]?.icon || '✨';
  const sourceLabel = (src) => SOURCE_META[src]?.label || src;

  // === Словари русификации для ленты событий ===

  // Сундуки (chest_code → русское название)
  const CHEST_RU = {
    bronze: 'Бронза',
    silver: 'Серебро',
    gold: 'Золото',
    platinum: 'Платина'
  };

  // Цвет/контраст сундука по коду
  const CHEST_COLORS = {
    bronze:   { color: '#cd7f32', textColor: '#fff' },
    silver:   { color: '#c0c0c0', textColor: '#000' },
    gold:     { color: '#FFD700', textColor: '#000' },
    platinum: { color: '#5ed1ff', textColor: '#003049' }
  };

  // Миссии (mission_code → русское описание)
  const MISSION_RU = {
    slots_diff_2: 'Сыграй в 2 разных слотах',
    slots_diff_3: 'Сыграй в 3 разных слотах',
    slots_diff_5: 'Сыграй в 5 разных слотах',
    scatter_any: 'Поймай скаттер',
    multiplier_x10: 'x10+ за спин',
    multiplier_x30: 'x30+ за спин',
    multiplier_x50: 'x50+ за спин (история)',
    multiplier_x100: 'x100+ (история)',
    bonus_x100: 'x100+ в бонус-игре',
    bonus_x200: 'x200+ в бонус-игре',
    bonus_games_5: '5 бонус-игр за день',
    moonstash_50: '50 SPERM в MoonStash',
    win_30x: '≥30x за спин',
    spins_150: '150 спинов за день',
    volume_500: '500 SPERM объёма',
    volume_1000: '1000 SPERM объёма',
    fs_trigger: 'Получил фриспины',
    buy_bonus_any: 'Купил Buy Bonus'
  };

  // Призы колеса фортуны (prize.code → русское описание)
  const WHEEL_PRIZE_RU = {
    stash_x2: 'MoonStash ×2',
    wager_x2: 'Пониженный отыгрыш x15',
    free_bb: 'Бонус-игра',
    free_bb_2: 'Бонус-игра',
    free_bb_3: 'Бонус-игра',
    discount_50: 'Скидка −50% на Buy Bonus',
    extra_spin: '+1 вращение колеса',
    zero: '0 SPERM (мимо)'
  };

  // Баффы (buff_type → русское описание)
  const BUFF_RU = {
    wager_x2: 'Пониженный отыгрыш x15',
    buy_bonus_discount_50: 'Скидка −50% на Buy Bonus',
    free_buy_bonus: 'Бесплатная Buy Bonus'
  };

  // Русификация приза колеса: sperm_N → "N SPERM", иначе по словарю, fallback на код
  const wheelPrizeLabel = (code) => {
    if (!code) return 'приз';
    const m = String(code).match(/^sperm_(\d+)$/i);
    if (m) return `${m[1]} SPERM`;
    return WHEEL_PRIZE_RU[code] || code;
  };

  // Целое число SPERM (для ленты — без дробей)
  const fmtInt = (v) => {
    const n = Math.round(parseFloat(v || 0));
    return n.toLocaleString('ru-RU');
  };

  // Читаемый ярлык пользователя для рефералки: @username → имя → id:telegram → ID.
  // Username может быть NULL — отдаём fallback.
  const refUserLabel = (u) => {
    if (!u) return '—';
    if (u.username) return `@${u.username}`;
    if (u.first_name) return u.first_name;
    if (u.telegram_id) return `id:${u.telegram_id}`;
    if (u.id) return `ID ${u.id}`;
    return '—';
  };

  // Цвет и иконка карточки события для ленты
  const feedEventMeta = (e) => {
    switch (e.type) {
      case 'claim_moonstash':
        return { icon: '💰', label: 'Сбор', color: '#FFD700', textColor: '#000' };
      case 'wheel_spin':
        return { icon: '🎡', label: 'Колесо', color: '#8b5cf6', textColor: '#fff' };
      case 'chest_open': {
        const code = (e.code || '').toLowerCase();
        const c = CHEST_COLORS[code] || { color: '#cd7f32', textColor: '#fff' };
        const name = CHEST_RU[code];
        return {
          icon: '📦',
          label: name || 'Сундук',
          ...c
        };
      }
      case 'mission_claim':
        return { icon: '🎯', label: 'Миссия', color: '#22c55e', textColor: '#fff' };
      case 'buff_activate':
        return { icon: '⚡', label: 'Бафф', color: '#38bdf8', textColor: '#fff' };
      case 'moonrush_payout':
        return { icon: '🏆', label: 'MoonRush', color: '#facc15', textColor: '#000' };
      case 'wager_complete': {
        const unlocked = parseFloat(e.amount || 0);
        // loyalty_deferred — новая модель (Б): кэшбэк выдан после отыгрыша
        // оборота своими ставками. Отдельный chip, без ×N.
        if (e.code === 'loyalty_deferred') {
          return { icon: '💰', label: 'КЭШБЭК', sublabel: 'ВЫДАН (Б)', color: '#22c55e', textColor: '#fff', highlight: true };
        }
        // boost_used пишется в loyalty_wager_complete_log в момент close.
        // Теперь = был ли сниженный отыгрыш с пафа (множитель кредита < базы
        // 20, т.е. x15). true → отыгран сниженный x15, false → базовый x20.
        // null → старые/неопределённые записи.
        const boost = e.details ? e.details.boost_used : null;
        const mult = boost === true ? '×15' : (boost === false ? '×20' : '×?');
        // Двухстрочный chip:
        // line1 — иконка + ВАГЕР/WAGER (короткое слово сверху)
        // line2 — статус + мульт ("ОТЫГРАН ×15" / "НЕ ВЗЯТ ×20")
        if (unlocked > 0) {
          return { icon: '🔓', label: 'WAGER', sublabel: `ОТЫГРАН ${mult}`, color: '#22c55e', textColor: '#fff', highlight: true };
        }
        return { icon: '🔒', label: 'ВАГЕР', sublabel: `НЕ ВЗЯТ ${mult}`, color: '#6b7280', textColor: '#fff' };
      }
      case 'slot_ref_invite':
        // 🤝 Реферал слотов: пригласитель привёл нового друга.
        return { icon: '🤝', label: 'РЕФЕРАЛ', sublabel: 'ПРИГЛАСИЛ', color: '#0ea5e9', textColor: '#fff' };
      case 'slot_ref_qualify':
        // 🤝 Реферал слотов: приглашённый прошёл квалификацию (награда выдана).
        return { icon: '🎯', label: 'РЕФЕРАЛ', sublabel: 'КВАЛИФ.', color: '#f97316', textColor: '#fff', highlight: true };
      case 'sugar_ref_invite': {
        // 🍬 SS-рефка: пригласил друга ($10 подарок). Мультик = тот же кошелёк/IP.
        const multi = e.details && (e.details.same_wallet || e.details.same_ip || e.details.ip_users >= 2);
        return multi
          ? { icon: '🍬', label: 'SS-РЕФ', sublabel: '⚠ МУЛЬТИК?', color: '#ef4444', textColor: '#fff', highlight: true }
          : { icon: '🍬', label: 'SS-РЕФ', sublabel: 'ПРИГЛАСИЛ', color: '#22c1a6', textColor: '#fff' };
      }
      case 'sugar_ref_qualify': {
        const multi = e.details && (e.details.same_wallet || e.details.same_ip);
        return multi
          ? { icon: '🍬', label: 'SS-РЕФ', sublabel: '⚠ $5 МУЛЬТИК?', color: '#ef4444', textColor: '#fff', highlight: true }
          : { icon: '🍬', label: 'SS-РЕФ', sublabel: 'КВАЛИФ. $5', color: '#f97316', textColor: '#fff', highlight: true };
      }
      case 'promo_activate': {
        // 🎟️ Активация промокода казино. ip_users ≥ 2 → подсветка мультика.
        const multi = e.details && e.details.ip_users >= 2;
        return multi
          ? { icon: '🎟️', label: 'ПРОМО', sublabel: '⚠ МУЛЬТИК?', color: '#ef4444', textColor: '#fff', highlight: true }
          : { icon: '🎟️', label: 'ПРОМО', sublabel: 'АКТИВ.', color: '#a855f7', textColor: '#fff' };
      }
      case 'promo_deposit_bonus':
        return { icon: '💎', label: 'ПРОМО', sublabel: 'ДЕПО-БОНУС', color: '#06b6d4', textColor: '#fff', highlight: true };
      case 'promo_aviator_win':
        return { icon: '✈️', label: 'ПРОМО', sublabel: 'ВЫИГРЫШ', color: '#f59e0b', textColor: '#000', highlight: true };
      default:
        return { icon: '✨', label: e.type, color: '#9ca3af', textColor: '#fff' };
    }
  };

  // Краткая строка-описание события для колонки «Подробности» (полностью на русском)
  const feedEventDetails = (e) => {
    const d = e.details || {};
    const amt = e.amount != null ? fmtInt(e.amount) : null;
    switch (e.type) {
      case 'claim_moonstash':
        return `+${amt} SPERM · отыгрыш после: ${fmtInt(d.wager_after || 0)} SPERM`;
      case 'wheel_spin': {
        const prizeLabel = wheelPrizeLabel(e.code);
        const prizeAmt = d.amount != null && parseFloat(d.amount) > 0
          ? ` · +${fmtInt(d.amount)} SPERM`
          : '';
        return `${prizeLabel}${prizeAmt}`;
      }
      case 'chest_open': {
        const code = (e.code || '').toLowerCase();
        const name = CHEST_RU[code] || code || '?';
        return `${name}${amt ? ` · +${amt} SPERM` : ''}`;
      }
      case 'mission_claim': {
        const desc = MISSION_RU[e.code] || e.code || '';
        const extra = d.spins ? ` · +${d.spins} вращений колеса` : '';
        return `${desc}${amt ? ` · +${amt} SPERM` : ''}${extra}`;
      }
      case 'buff_activate': {
        const name = BUFF_RU[e.code] || e.code || '';
        const until = d.expires_at ? ` · до ${formatDate(d.expires_at)}` : '';
        return `${name}${until}`;
      }
      case 'moonrush_payout': {
        const vol = d.place_vol;
        const spins = d.place_spins;
        let place = '';
        if (vol && spins) place = `Топ-${vol} (объём) + Топ-${spins} (спинов)`;
        else if (vol) place = `Топ-${vol} по объёму`;
        else if (spins) place = `Топ-${spins} по спинам`;
        else place = 'без места';
        return `+${amt} SPERM · ${place}`;
      }
      case 'wager_complete': {
        // loyalty_deferred — новая модель (Б): кэшбэк выдан после отыгрыша
        // оборота СВОИМИ ставками (не «разморожен» с баланса, а зачислен).
        const isDeferred = e.code === 'loyalty_deferred';
        const src = e.code === 'piggy' ? 'КОПИЛКА'
          : isDeferred ? 'КЭШБЭК (Б)' : 'ЛОЯЛЬНОСТЬ';
        const progressNum = parseFloat(d.progress || 0);
        const requiredNum = parseFloat(d.required || 0);
        const progress = fmtInt(progressNum);
        const required = fmtInt(requiredNum);
        const pct = requiredNum > 0
          ? Math.min(100, Math.round((progressNum / requiredNum) * 100))
          : 0;
        const unlocked = parseFloat(e.amount || 0);
        if (isDeferred) {
          return `${src} · оборот своими ${progress}/${required} SPERM (${pct}%) · выдан кэшбэк ${fmtNum(unlocked)} SPERM`;
        }
        if (unlocked > 0) {
          return `${src} · отыграл ${progress}/${required} SPERM (${pct}%) · разморожено ${fmtNum(unlocked)} SPERM`;
        }
        return `${src} · отыграл ${progress}/${required} SPERM (${pct}%) · разморожено 0 (всё ушло в проигрыш)`;
      }
      case 'slot_ref_invite': {
        // Пригласитель = текущий юзер строки (e.username), приглашённый — в details.
        const friend = refUserLabel({
          username: d.referred_username,
          first_name: d.referred_first_name,
          telegram_id: d.referred_telegram_id,
          id: d.referred_id
        });
        return `Пригласил друга ${friend}`;
      }
      case 'slot_ref_qualify': {
        const friend = refUserLabel({
          username: d.referred_username,
          first_name: d.referred_first_name,
          telegram_id: d.referred_telegram_id,
          id: d.referred_id
        });
        return `Друг ${friend} прошёл квалификацию · награда: серебряный сундук + 20 ФС + 2 колеса + 50 SPERM`;
      }
      case 'sugar_ref_invite': {
        // 🍬 SS-рефка: пригласил друга ($10 локнутый подарок). Флаги мультов — для тебя.
        const friend = refUserLabel({
          username: d.referred_username, first_name: d.referred_first_name,
          telegram_id: d.referred_telegram_id, id: d.referred_id });
        const flags = [];
        if (d.same_wallet) flags.push('тот же КОШЕЛЁК');
        if (d.same_ip) flags.push('тот же IP');
        if (d.ip_users >= 2 && !d.same_ip) flags.push(`${d.ip_users} акк с IP`);
        const root = (flags.length && d.root_username) ? ` · корневой акк: @${d.root_username}` : '';
        const warn = flags.length ? ` · ⚠ ${flags.join(', ')}${root}` : '';
        return `Пригласил друга ${friend} · подарок $${d.gift || 10}${warn}`;
      }
      case 'sugar_ref_qualify': {
        const friend = refUserLabel({
          username: d.referred_username, telegram_id: d.referred_telegram_id, id: d.referred_id });
        const flags = [];
        if (d.same_wallet) flags.push('тот же КОШЕЛЁК');
        if (d.same_ip) flags.push('тот же IP');
        const warn = flags.length ? ` · ⚠ ${flags.join(', ')}` : '';
        return `Друг ${friend} прошёл квалификацию · рефереру +$${d.reward_usd || 5} (деп $${fmtNum(d.deposited || 0)}, оборот $${fmtNum(d.wagered || 0)})${warn}`;
      }
      case 'promo_activate': {
        const code = e.code || '?';
        const rewards = Array.isArray(d.rewards) ? d.rewards : [];
        const summary = rewards.map((r) => {
          switch (r.type) {
            case 'freespins': return `🎰×${r.count}`;
            case 'sperm': return `💰${r.amount}`;
            case 'wheel_spins': return `🎡×${r.count}`;
            case 'chest': return `🎁${r.chestCode}`;
            case 'free_buy_bonus': return `🛒×${r.count}`;
            case 'aviator_flights': return `✈️×${r.count}`;
            case 'deposit_bonus': return `💎${r.percent}%`;
            default: return r.type;
          }
        }).join(' · ');
        const ip = d.ip ? ` · IP ${d.ip}` : '';
        const multi = d.ip_users >= 2 ? ` · ⚠ ${d.ip_users} акк с этого IP` : '';
        return `Код ${code} · ${summary || 'награды'}${ip}${multi}`;
      }
      case 'promo_deposit_bonus':
        return `Код ${e.code || '?'} · депозит ${fmtInt(d.deposit)} → бонус +${amt} SPERM (${fmtInt(d.percent)}%)`;
      case 'promo_aviator_win':
        return `Авиатор-фрибеты · ${d.flights || 0}/${d.total || 0} полётов · +${amt} SPERM`;
      default:
        return JSON.stringify(d);
    }
  };

  // Эмодзи-медаль для топ-3 строк
  const rankMedal = (idx) => {
    if (idx === 0) return '🥇';
    if (idx === 1) return '🥈';
    if (idx === 2) return '🥉';
    return `#${idx + 1}`;
  };

  // Рендер одной карточки топа: { title, icon, rows, showCount, valueLabel }
  const renderLeadersCard = ({ title, icon, rows, showCount = false, valueLabel = 'SPERM' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {icon} {title}
        </Typography>
        {!rows || !rows.length ? (
          <Alert severity="info" sx={{ py: 0.5 }}>Нет данных</Alert>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 } }}>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={`${r.user_id}-${idx}`}>
                    <TableCell sx={{ width: 40, fontSize: '0.75rem' }}>
                      {rankMedal(idx)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                      {r.username ? `@${r.username}` : (r.first_name || `ID ${r.user_id}`)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {fmtNum(r.total ?? r.amount)} {valueLabel}
                    </TableCell>
                    {showCount && (
                      <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap', width: 50 }}>
                        ×{r.count || 0}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const data = overview || {};
  const wagering = data.wagering || {};
  const moonRush = data.moonRush || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Заголовок — адаптив: на xs/sm колонка, на md+ строка */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1.5, md: 0 },
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Casino sx={{ color: '#FFD700', fontSize: { xs: 24, md: 32 } }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', md: '2.125rem' } // h5 -> h4
            }}
          >
            Лояльность слотов
          </Typography>
          <Chip label={`Phase ${data.phase || 1}`} size="small" color="warning" sx={{ ml: { xs: 0, md: 1 } }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={data.enabled || false}
                onChange={handleToggle}
                color="warning"
              />
            }
            label={data.enabled ? 'Включено' : 'Выключено'}
          />
          <Tooltip title="Обновить">
            <IconButton onClick={loadAll}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

{/* Табы — на узких экранах уменьшаем padding кнопок, чтобы влазили */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', md: 90 },
              px: { xs: 1.25, md: 2 },
              fontSize: { xs: '0.78rem', md: '0.875rem' }
            }
          }}
        >
          <Tab label="Лента событий" />
          <Tab label="Обзор" />
          <Tab label="Лидерборд MoonRush" />
          <Tab label="Выплаты MoonRush" />
          <Tab label="Тест-гранты" />
          <Tab label="Настройки" />
          <Tab label="🤝 Рефералы" />
        </Tabs>
      </Paper>

      {/* Таб 1: Обзор (после Ленты) */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Карточки */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">MoonStash — общий пул</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                  {fmtNum(data.totalPool)}
                </Typography>
                <Typography variant="caption" color="text.secondary">SPERM в копилке</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Активный wager</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {wagering.activeUsers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  юзеров · заблокировано {fmtNum(wagering.totalLocked)} SPERM
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">MoonRush — пул недели</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                  {fmtNum(moonRush.pool)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {moonRush.weekStart
                    ? `неделя с ${new Date(moonRush.weekStart).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Moscow' })}`
                    : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Отложенный кэшбэк (Вариант Б) — в отыгрыше, не на балансе */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Кэшбэк в отыгрыше (Б)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#22c55e' }}>
                  {wagering.pendingUsers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  юзеров · к выдаче {fmtNum(wagering.totalPending)} SPERM
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Кто сейчас в отыгрыше — поимённо. Выше только счётчик и сумма,
              а нужно видеть, у кого сколько залок и сколько он выведет. */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Кто в отыгрыше{wageringTotal ? ` · ${wageringTotal}` : ''}
                </Typography>
                {!wageringList.length ? (
                  <Alert severity="info">Сейчас никто не отыгрывает</Alert>
                ) : (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Игрок</TableCell>
                            <TableCell align="right">В отыгрыше</TableCell>
                            <TableCell align="right">Прогресс</TableCell>
                            <TableCell align="right">Осталось прокрутить</TableCell>
                            <TableCell align="right">К выводу</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {wageringList.map(u => (
                            <TableRow key={u.id}>
                              <TableCell>
                                <Typography variant="body2">
                                  {u.username ? `@${u.username}` : `id ${u.id}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{fmtNum(u.locked)}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={`${u.percent}%`}
                                  color={u.percent >= 100 ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">{fmtNum(u.remaining)}</TableCell>
                              <TableCell align="right">{fmtNum(u.available)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {wageringTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                          count={wageringTotalPages}
                          page={wageringPage}
                          onChange={(_, p) => loadWagering(p)}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Источники начислений */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Источники начислений MoonStash</Typography>
                {!data.sources?.length ? (
                  <Alert severity="info">Пока нет начислений</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Источник</TableCell>
                          <TableCell align="right">Всего, SPERM</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.sources.map(s => (
                          <TableRow key={s.source}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{sourceIcon(s.source)}</span>
                                <Typography variant="body2">{sourceLabel(s.source)}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {fmtNum(s.total)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Последние claim */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Последние claim ({data.recentClaims?.length || 0})
                </Typography>
                {!data.recentClaims?.length ? (
                  <Alert severity="info">Пока нет клеймов</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Пользователь</TableCell>
                          <TableCell align="right">Получил</TableCell>
                          <TableCell align="right">Wager после</TableCell>
                          <TableCell>Когда</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.recentClaims.slice(0, 100).map(c => (
                          <TableRow key={c.id}>
                            <TableCell>
                              {c.username ? `@${c.username}` : c.firstName || `ID ${c.userId}`}
                            </TableCell>
                            <TableCell align="right">{fmtNum(c.amount)}</TableCell>
                            <TableCell align="right">{fmtNum(c.wagerRequiredAfter)}</TableCell>
                            <TableCell>
                              <Typography variant="caption">{formatDate(c.createdAt)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Лидеры лояльности — топы юзеров по разным метрикам */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1, mt: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                🏆 Лидеры лояльности
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadLeaders}
                disabled={leadersLoading}
              >
                Обновить
              </Button>
            </Box>
          </Grid>

          {leadersLoading && !leaders ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по общему приходу',
                  icon: '🏆',
                  rows: leaders?.byTotal
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по рейкбэку',
                  icon: '⚡',
                  rows: leaders?.byRakeback
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по призам колеса',
                  icon: '🎡',
                  rows: leaders?.byWheel,
                  showCount: true
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по сундукам',
                  icon: '📦',
                  rows: leaders?.byChest,
                  showCount: true
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по миссиям',
                  icon: '🎯',
                  rows: leaders?.byMission,
                  showCount: true
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по MoonRush',
                  icon: '🌙',
                  rows: leaders?.byMoonRush,
                  showCount: true
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Топ по claim-ам',
                  icon: '💸',
                  rows: leaders?.byClaim,
                  showCount: true
                })}
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                {renderLeadersCard({
                  title: 'Самые везучие (1 приз)',
                  icon: '🍀',
                  rows: leaders?.luckiestWheel
                })}
              </Grid>
            </>
          )}
        </Grid>
      </TabPanel>

      {/* Таб 0: Лента событий (первая вкладка) */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 2
            }}>
              <Typography variant="h6">
                <Timeline sx={{ verticalAlign: 'middle', color: '#8b5cf6', mr: 1 }} />
                Лента событий лояльности
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  (авто-обновление 15 сек, последние {feed.length})
                </Typography>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => loadFeed()}
                disabled={feedLoading}
              >
                Обновить
              </Button>
            </Box>

            {/* Фильтры ленты */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Тип события</InputLabel>
                <Select
                  label="Тип события"
                  value={feedFilters.type}
                  onChange={(e) => setFeedFilters(f => ({
                    ...f,
                    type: e.target.value,
                    // Если уходим с wager_complete — обнуляем суб-фильтры
                    ...(e.target.value !== 'wager_complete' && { source: '', wager_status: '' })
                  }))}
                >
                  <MenuItem value="">Все типы</MenuItem>
                  <MenuItem value="wager_complete">🔓 Wager (отыгрыш)</MenuItem>
                  <MenuItem value="claim_moonstash">💰 Сбор MoonStash</MenuItem>
                  <MenuItem value="wheel_spin">🎡 Колесо</MenuItem>
                  <MenuItem value="chest_open">📦 Сундук</MenuItem>
                  <MenuItem value="mission_claim">🎯 Миссия</MenuItem>
                  <MenuItem value="buff_activate">⚡ Бафф</MenuItem>
                  <MenuItem value="moonrush_payout">🏆 MoonRush</MenuItem>
                  <MenuItem value="slot_ref_invite">🤝 Реферал: приглашение</MenuItem>
                  <MenuItem value="slot_ref_qualify">🎯 Реферал: квалификация</MenuItem>
                  <MenuItem value="promo_activate">🎟️ Промо: активация</MenuItem>
                  <MenuItem value="promo_deposit_bonus">💎 Промо: депо-бонус</MenuItem>
                  <MenuItem value="promo_aviator_win">✈️ Промо: выигрыш фрибетов</MenuItem>
                </Select>
              </FormControl>

              {feedFilters.type === 'wager_complete' && (
                <>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Источник вагера</InputLabel>
                    <Select
                      label="Источник вагера"
                      value={feedFilters.source}
                      onChange={(e) => setFeedFilters(f => ({ ...f, source: e.target.value }))}
                    >
                      <MenuItem value="">Любой</MenuItem>
                      <MenuItem value="loyalty_deferred">Кэшбэк (Б)</MenuItem>
                      <MenuItem value="loyalty">Лояльность (старое)</MenuItem>
                      <MenuItem value="piggy">Копилка</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Статус</InputLabel>
                    <Select
                      label="Статус"
                      value={feedFilters.wager_status}
                      onChange={(e) => setFeedFilters(f => ({ ...f, wager_status: e.target.value }))}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="taken">✅ Взят (отыгран)</MenuItem>
                      <MenuItem value="not_taken">🔒 Не взят</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {(feedFilters.type || feedFilters.source || feedFilters.wager_status) && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setFeedFilters({ type: '', source: '', wager_status: '' })}
                >
                  Сбросить фильтры
                </Button>
              )}
            </Stack>

            {feedLoading && !feed.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !feed.length ? (
              <Alert severity="info">Пока событий нет</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.05)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.65rem', width: 48 }}>Время</TableCell>
                      <TableCell sx={{ fontSize: '0.65rem', width: 80 }}>Юзер</TableCell>
                      <TableCell sx={{ fontSize: '0.65rem' }}>Событие</TableCell>
                      <TableCell sx={{ fontSize: '0.65rem' }}>Детали</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feed.map((e, i) => {
                      const meta = feedEventMeta(e);
                      return (
                        <TableRow
                          key={`${e.type}-${e.user_id}-${e.ts}-${i}`}
                          sx={{
                            borderLeft: meta.highlight
                              ? `6px solid ${meta.color}`
                              : `3px solid ${meta.color}`,
                            ...(meta.highlight && {
                              background: 'linear-gradient(90deg, rgba(34,197,94,0.25) 0%, rgba(34,197,94,0.05) 100%)',
                              boxShadow: 'inset 0 0 0 1px rgba(34,197,94,0.4)',
                              '& td': { fontWeight: 700 }
                            })
                          }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap', width: 48 }}>
                            {e.ts ? (() => {
                              const d = new Date(e.ts);
                              return (
                                <>
                                  <Typography sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem', color: '#cbd5e1' }}>
                                    {d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                                  </Typography>
                                  <Typography sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem', color: '#64748b' }}>
                                    {d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                </>
                              );
                            })() : <Typography variant="caption">—</Typography>}
                          </TableCell>
                          <TableCell sx={{ width: 80, fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 80 }}>
                            {e.username
                              ? `@${e.username}`
                              : e.first_name || `ID ${e.user_id}`}
                          </TableCell>
                          <TableCell sx={{ width: 110, p: 0.5 }}>
                            <Chip
                              label={
                                meta.sublabel ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, py: 0.25 }}>
                                    <Box sx={{ fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      {meta.icon} {meta.label}
                                    </Box>
                                    <Box sx={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.95, mt: 0.25 }}>
                                      {meta.sublabel}
                                    </Box>
                                  </Box>
                                ) : (
                                  `${meta.icon} ${meta.label}`
                                )
                              }
                              size="small"
                              sx={{
                                bgcolor: meta.color,
                                color: meta.textColor,
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: meta.sublabel ? 'auto' : 22,
                                py: meta.sublabel ? 0.25 : 0,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.2 }}>
                              {feedEventDetails(e)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Кнопка "Показать ещё" */}
            {feed.length > 0 && feedHasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadMoreFeed}
                  disabled={feedLoading}
                  startIcon={feedLoading ? <CircularProgress size={14} /> : null}
                >
                  Показать ещё (загружено {feed.length})
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Таб 2: Лидерборд */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <EmojiEvents sx={{ verticalAlign: 'middle', color: '#FFD700', mr: 1 }} />
              MoonRush — топ текущей недели
              {leaderboard?.weekStart && (
                <Chip
                  size="small"
                  label={`Неделя с ${leaderboard.weekStart}`}
                  sx={{ ml: 1 }}
                />
              )}
              {leaderboard?.pool != null && (
                <Chip
                  size="small"
                  color="primary"
                  label={`Пул: ${fmtNum(leaderboard.pool)} SPERM`}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {(!leaderboard ||
              ((leaderboard.topByVolume || []).length === 0 &&
               (leaderboard.topBySpins  || []).length === 0)) ? (
              <Alert severity="info">Пока нет участников этой недели</Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Топ по объёму */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    🏆 Топ по объёму ставок
                  </Typography>
                  <TableContainer>
                    <Table
                      size="small"
                      sx={{
                        width: 'auto',
                        '& .MuiTableCell-root': {
                          px: { xs: 0.75, sm: 1.5 },
                          py: 0.5,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 56 }}>#</TableCell>
                          <TableCell>Игрок</TableCell>
                          <TableCell align="right">SPERM</TableCell>
                          <TableCell align="right">Спины</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(leaderboard.topByVolume || []).slice(0, 10).map((row, idx) => (
                          <TableRow key={`v-${row.userId || idx}`}>
                            <TableCell>
                              <Chip
                                label={`#${idx + 1}`}
                                size="small"
                                color={idx < 3 ? 'warning' : 'default'}
                                variant={idx < 3 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.username ? `@${row.username}` :
                                (row.firstName || `ID ${row.userId}`)}
                            </TableCell>
                            <TableCell align="right">{fmtNum(row.totalWager)}</TableCell>
                            <TableCell align="right">{row.totalSpins || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Топ по спинам */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    ⚡ Топ по спинам
                  </Typography>
                  <TableContainer>
                    <Table
                      size="small"
                      sx={{
                        width: 'auto',
                        '& .MuiTableCell-root': {
                          px: { xs: 0.75, sm: 1.5 },
                          py: 0.5,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 56 }}>#</TableCell>
                          <TableCell>Игрок</TableCell>
                          <TableCell align="right">Спины</TableCell>
                          <TableCell align="right">SPERM</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(leaderboard.topBySpins || []).slice(0, 10).map((row, idx) => (
                          <TableRow key={`s-${row.userId || idx}`}>
                            <TableCell>
                              <Chip
                                label={`#${idx + 1}`}
                                size="small"
                                color={idx < 3 ? 'warning' : 'default'}
                                variant={idx < 3 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.username ? `@${row.username}` :
                                (row.firstName || `ID ${row.userId}`)}
                            </TableCell>
                            <TableCell align="right">{row.totalSpins || 0}</TableCell>
                            <TableCell align="right">{fmtNum(row.totalWager)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Таб 3: Архив выплат MoonRush с пагинацией */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6">Архив выплат MoonRush</Typography>
              <Typography variant="body2" color="text.secondary">
                Всего записей: {payoutsTotal}
              </Typography>
            </Box>
            {payoutsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !payouts.length ? (
              <Alert severity="info">Пока нет выплат</Alert>
            ) : (
              <>
                {(() => {
                  // Группируем записи текущей страницы по week_start
                  const groups = [];
                  for (const p of payouts) {
                    const wk = p.weekStart;
                    if (!groups.length || groups[groups.length - 1].weekStart !== wk) {
                      groups.push({ weekStart: wk, rows: [p] });
                    } else {
                      groups[groups.length - 1].rows.push(p);
                    }
                  }

                  const fmtWeekLabel = (weekStart) => {
                    if (!weekStart) return '—';
                    const start = new Date(weekStart);
                    const end = new Date(start);
                    end.setDate(start.getDate() + 6);
                    const fmt = (d) => d.toLocaleDateString('ru-RU', {
                      day: '2-digit', month: '2-digit', year: 'numeric'
                    });
                    return `Неделя ${fmt(start)} — ${fmt(end)}`;
                  };

                  return groups.map((g) => (
                    <Box key={g.weekStart} sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}
                      >
                        {fmtWeekLabel(g.weekStart)} · {g.rows.length} выплат
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Пользователь</TableCell>
                              <TableCell align="center">По объёму</TableCell>
                              <TableCell align="center">По спинам</TableCell>
                              <TableCell align="right">Выплата, SPERM</TableCell>
                              <TableCell align="center">Статус</TableCell>
                              <TableCell>Когда</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {g.rows.map((p) => (
                              <TableRow key={p.id}>
                                <TableCell>
                                  {p.username ? `@${p.username}` : p.firstName || `ID ${p.userId}`}
                                </TableCell>
                                <TableCell align="center">
                                  {p.placeVolume ? <Chip label={`#${p.placeVolume}`} size="small" color="warning" /> : '—'}
                                </TableCell>
                                <TableCell align="center">
                                  {p.placeSpins ? <Chip label={`#${p.placeSpins}`} size="small" color="info" /> : '—'}
                                </TableCell>
                                <TableCell align="right">{fmtNum(p.payout)}</TableCell>
                                <TableCell align="center">
                                  {p.disqualified
                                    ? <Tooltip title={p.disqualifyReason ? JSON.stringify(p.disqualifyReason) : ''}>
                                        <Chip label="Дисквал." size="small" color="error" />
                                      </Tooltip>
                                    : <Chip label="OK" size="small" color="success" variant="outlined" />}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{formatDate(p.paidAt)}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ));
                })()}

                {payoutsTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={payoutsTotalPages}
                      page={payoutsPage}
                      onChange={(_, p) => loadPayouts(p)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Таб 4: Тест-гранты */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Зачислить SPERM в MoonStash
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Только для тестирования. Сумма зачислится в копилку без wager-блокировки.
                </Alert>
                <TextField
                  fullWidth label="userId (users.id, не telegram_id)"
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  size="small" sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth label="Сумма SPERM"
                  type="number"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  size="small" sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth label="Источник"
                  value={grantSource}
                  onChange={(e) => setGrantSource(e.target.value)}
                  size="small" sx={{ mb: 2 }}
                  helperText="например: admin_adjust, slot_play, free_spin_grant"
                />
                <Button
                  variant="contained" color="warning" fullWidth
                  startIcon={<Bolt />}
                  onClick={handleGrant}
                >
                  Дать SPERM в MoonStash
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Добавить вращения колеса
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Для тестирования механики колеса фортуны.
                </Alert>
                <TextField
                  fullWidth label="userId"
                  value={grantSpinsUserId}
                  onChange={(e) => setGrantSpinsUserId(e.target.value)}
                  size="small" sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth label="Сколько вращений"
                  type="number"
                  value={grantSpinsCount}
                  onChange={(e) => setGrantSpinsCount(e.target.value)}
                  size="small" sx={{ mb: 2 }}
                />
                <Button
                  variant="contained" color="primary" fullWidth
                  startIcon={<Bolt />}
                  onClick={handleGrantSpins}
                >
                  Дать вращения колеса
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Таб 5: Настройки */}
      <TabPanel value={tabValue} index={5}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Настройки лояльности (category = loyalty)</Typography>
            {!settings.length ? (
              <Alert severity="info">Нет настроек в категории loyalty</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ключ</TableCell>
                      <TableCell>Значение</TableCell>
                      <TableCell>Описание</TableCell>
                      <TableCell align="right">Действие</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.map(row => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {row.key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <TextField
                            size="small" fullWidth
                            value={settingCurrentValue(row)}
                            onChange={(e) => handleSettingChange(row.key, e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small" variant="outlined"
                            startIcon={<Save />}
                            disabled={settingsDraft[row.key] === undefined}
                            onClick={() => handleSettingSave(row)}
                          >
                            Сохранить
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Значение пишется как строка через универсальный endpoint /admin/settings/update.
              Тип определяется по существующему value_* в БД.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Таб 6: 🤝 Реферальная система слотов */}
      <TabPanel value={tabValue} index={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🤝 Реферальная система слотов
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              (квалификация: ≥{refStats?.qualifyRule?.minDeposit || 100} SPERM и оборот ≥{refStats?.qualifyRule?.minWager || 700} SPERM)
            </Typography>
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadReferrals}
            disabled={refLoading}
          >
            Обновить
          </Button>
        </Box>

        {refLoading && !refStats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* === Карточки статистики === */}
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Всего приглашено</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0ea5e9' }}>
                    {refStats?.totalInvited ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">друзей привязано</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Квалифицировано</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#22c55e' }}>
                    {refStats?.totalQualified ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">прошли квалификацию</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Конверсия</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f97316' }}>
                    {refStats?.conversion ?? 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">квалиф. / приглашённых</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Пригласителей</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                    {refStats?.totalReferrers ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">уникальных юзеров</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* === Топ-пригласители === */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>🏆 Топ-пригласители</Typography>
                  {!refTop.length ? (
                    <Alert severity="info">Пока нет пригласителей</Alert>
                  ) : (
                    <TableContainer sx={{ maxHeight: 480 }}>
                      <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 1, py: 0.5 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40 }}>#</TableCell>
                            <TableCell>Пригласитель</TableCell>
                            <TableCell align="right">Привёл</TableCell>
                            <TableCell align="right">Квалиф.</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {refTop.map((r, idx) => (
                            <TableRow key={`${r.referrerId}-${idx}`}>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{rankMedal(idx)}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                                {refUserLabel({ username: r.username, first_name: r.firstName, telegram_id: r.telegramId, id: r.referrerId })}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{r.invited}</TableCell>
                              <TableCell align="right">
                                <Chip label={r.qualified} size="small"
                                  color={r.qualified > 0 ? 'success' : 'default'}
                                  variant={r.qualified > 0 ? 'filled' : 'outlined'} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* === Лента реферальных событий === */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Timeline sx={{ verticalAlign: 'middle', color: '#0ea5e9', mr: 1 }} />
                    Лента реферальных событий
                  </Typography>
                  {!refFeed.length ? (
                    <Alert severity="info">Пока нет событий</Alert>
                  ) : (
                    <TableContainer sx={{ maxHeight: 480 }}>
                      <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.65rem', width: 48 }}>Время</TableCell>
                            <TableCell sx={{ fontSize: '0.65rem' }}>Событие</TableCell>
                            <TableCell sx={{ fontSize: '0.65rem' }}>Пригласитель → Друг</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {refFeed.map((e, i) => {
                            const isQualify = e.type === 'slot_ref_qualify';
                            const color = isQualify ? '#f97316' : '#0ea5e9';
                            return (
                              <TableRow key={`${e.type}-${e.referrerId}-${e.referredId}-${i}`}
                                sx={{ borderLeft: `3px solid ${color}` }}>
                                <TableCell sx={{ whiteSpace: 'nowrap', width: 48 }}>
                                  {e.ts ? (() => {
                                    const d = new Date(e.ts);
                                    return (
                                      <>
                                        <Typography sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem', color: '#cbd5e1' }}>
                                          {d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                                        </Typography>
                                        <Typography sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem', color: '#64748b' }}>
                                          {d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                      </>
                                    );
                                  })() : '—'}
                                </TableCell>
                                <TableCell sx={{ width: 100 }}>
                                  <Chip
                                    label={isQualify ? '🎯 Квалиф.' : '🤝 Привёл'}
                                    size="small"
                                    sx={{ bgcolor: color, color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 22 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.72rem' }}>
                                  {refUserLabel({ username: e.referrerUsername, first_name: e.referrerFirstName, telegram_id: e.referrerTelegramId, id: e.referrerId })}
                                  {' → '}
                                  {refUserLabel({ username: e.referredUsername, first_name: e.referredFirstName, telegram_id: e.referredTelegramId, id: e.referredId })}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* === Полный список пар === */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Typography variant="h6">Все реферальные пары</Typography>
                    <Typography variant="body2" color="text.secondary">Всего: {refPairsTotal}</Typography>
                  </Box>
                  {!refPairs.length ? (
                    <Alert severity="info">Пока нет пар</Alert>
                  ) : (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Пригласитель</TableCell>
                              <TableCell>Приглашённый</TableCell>
                              <TableCell align="center">Статус / прогресс</TableCell>
                              <TableCell>Привязан</TableCell>
                              <TableCell>Квалиф.</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {refPairs.map((p) => (
                              <TableRow key={p.id}>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                  {refUserLabel({ username: p.referrerUsername, first_name: p.referrerFirstName, telegram_id: p.referrerTelegramId, id: p.referrerId })}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                  {refUserLabel({ username: p.referredUsername, first_name: p.referredFirstName, telegram_id: p.referredTelegramId, id: p.referredId })}
                                </TableCell>
                                <TableCell align="center">
                                  {p.qualified ? (
                                    <Chip label="✅ Квалифицирован" size="small" color="success" />
                                  ) : (
                                    <Tooltip title={`Пополнено ${fmtNum(p.depositedTotal)} из ${p.depositNeed} SPERM · оборот ${fmtNum(p.wageredTotal)} из ${p.wagerNeed} SPERM (спинов: ${p.spinsCount})`}>
                                      <Chip
                                        label={`${fmtInt(p.depositedTotal)}/${p.depositNeed} монет · оборот ${fmtInt(p.wageredTotal)}/${p.wagerNeed}`}
                                        size="small" color="warning" variant="outlined"
                                      />
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{formatDate(p.createdAt)}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{p.qualifiedAt ? formatDate(p.qualifiedAt) : '—'}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {refPairsTotalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination
                            count={refPairsTotalPages}
                            page={refPairsPage}
                            onChange={(_, pg) => loadReferralPairs(pg)}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Loyalty;
